import {
  useState,
  useEffect,
  useContext,
  useCallback,
  lazy,
  Suspense,
  useMemo,
} from 'react'
import { useMediaQuery } from 'react-responsive'
import { toast, Bounce } from 'react-toastify'
import { useLocation, useNavigate } from 'react-router-dom'
import MeService from './services/me-service'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

const Sidenav = lazy(() => import('components/Sidenav'))
const ParticlesDiv = lazy(() => import('layouts/particles'))
const Notification = lazy(() => import('components/Notification'))
const AppRoutes = lazy(() => import('routes.js'))

import sidenavRoutes from 'sidenav-routes.js'

import theme from 'assets/theme'
import themeDark from 'assets/theme-dark'

import { useMaterialUIController, AuthContext, useAuthContext } from 'context'

import posthog from 'posthog-js'
import { jwtDecode } from 'jwt-decode'

import { setupAxiosInterceptors } from './services/interceptor.js'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Spinner from 'components/Spinner/index.js'
import { ColorSchema } from 'color-schema.js'
import { TourProvider } from './context/TourContext'
import Button from './components/Button'
import pages from 'constants/pages'
import { removeTutorialOverlay } from 'utils'
import { settings } from 'settings'

export default function App() {
  const authContext = useContext(AuthContext)
  const { userDetails, setUserDetails } = useAuthContext()

  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller
  const [onMouseEnter, setOnMouseEnter] = useState(false)
  const [notification, setNotification] = useState(undefined)
  // const [notification, setNotification] = useState({
  //   type: 'feedback'
  // })
  const { pathname } = useLocation()

  const isTablet = useMediaQuery({
    query: '(max-width: 1200px)',
  })

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = useCallback(() => {
    if (miniSidenav && !onMouseEnter) {
      // setMiniSidenav(dispatch, false);
      setOnMouseEnter(true)
    }
  }, [miniSidenav, onMouseEnter])

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = useCallback(() => {
    if (!isTablet) {
      if (onMouseEnter) {
        setOnMouseEnter(false)
      }
      // setMiniSidenav(dispatch, true);
    }
  }, [onMouseEnter, isTablet])

  // if the token expired or other errors it logs out and goes to the login page

  setupAxiosInterceptors(
    () => {
      if (authContext.isAuthenticated) {
        localStorage.removeItem('token')
        sessionStorage.removeItem('token')
        window.location = `${process.env.REACT_APP_URL}auth/login`
      }
    },
    notif => {
      setNotification(notif)
    },
  )

  const closeNotification = () => {
    setNotification(undefined)
  }

  useEffect(() => {
    if (process.env.REACT_APP_DEVELOPMENT !== 'true') {
      posthog.init('phc_aQJcY2YnVpw8FaJXYU1OZFTy4jfFzy3H0qOKiJMzN16', {
        api_host: process.env.REACT_APP_POSTHOG_URL,
        person_profiles: 'identified_only',
      })
    }

    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token')

    if (token) {
      const decoded = jwtDecode(token)

      posthog.identify(`${decoded.id}#${decoded.email}`, {
        id: decoded.id,
        email: decoded.email,
      })
    }
  }, [])

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction)
  }, [direction])

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0
    document.scrollingElement.scrollTop = 0
  }, [pathname])

  useEffect(() => {
    const selectedTheme = localStorage.getItem('theme')
    if (selectedTheme) {
      document.body.classList.add(selectedTheme)
    } else {
      document.body.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    }
  }, [])
  const { theme: tailwindTheme } = controller

  const navigate = useNavigate()

  const isAppInTestingPhase = !!process.env.REACT_APP_TESTING

  const shouldShowToast = useMemo(() => {
    if (isAppInTestingPhase) {
      return false
    }

    const lastBannerTimestamp = parseInt(
      userDetails.lastCommunityBannerTimestamp,
    )
    const now = Math.floor(Date.now() / 1000)
    const differenceInDays = (now - lastBannerTimestamp) / (60 * 60 * 24)

    return differenceInDays >= 7
  }, [isAppInTestingPhase, userDetails.lastCommunityBannerTimestamp])

  const handleUpdateWantInCommunity = async () => {
    try {
      await MeService.updateWantInCommunity({ want_in_community: true })
    } catch (error) {
      console.error(error)
    }
  }

  const handleUpdateLastCommunityBannerTimestamp = async () => {
    try {
      await MeService.updateLastTimeCommunityBanner()
    } catch (error) {
      console.error(error)
    }
  }

  const toastContent = useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="font-bold text-over-secondary-background text-sm">
          Hai în comunitatea de WhatsApp pentru a fi la curent cu toate
          noutatile legate de {settings.exam_type}!
        </div>
        <div className="flex justify-center items-center">
          <Button
            className="bg-btn-primary py-2 mr-2"
            onClick={handleUpdateWantInCommunity}
          >
            Vreau în comunitate
          </Button>
          <Button
            className="bg-btn-primary py-2 ml-2"
            onClick={handleUpdateLastCommunityBannerTimestamp}
          >
            Închide
          </Button>
        </div>
      </div>
    )
  }, [])

  useEffect(() => {
    const handleWantInCommunityToast = async () => {
      try {
        const showToast = shouldShowToast
        if (
          !userDetails.wantInCommunity &&
          showToast &&
          userDetails.hasDetails
        ) {
          toast.info(toastContent, {
            position: 'top-right',
            autoClose: false,
            onClose: handleUpdateLastCommunityBannerTimestamp,
            hideProgressBar: true,
            icon: false,
            type: 'success',
            pauseOnHover: true,
            style: {
              backgroundColor: 'rgba(var(--secondary-background))',
            },
            draggable: true,
            progress: undefined,
            transition: Bounce,
          })
        }
      } catch (error) {
        console.error(error)
      }
    }
    handleWantInCommunityToast()
  }, [
    userDetails.lastCommunityBannerTimestamp,
    userDetails.wantInCommunity,
    userDetails.hasDetails,
    shouldShowToast,
    toastContent,
  ])

  useEffect(() => {
    if (userDetails.tutorial) {
      removeTutorialOverlay()
    }
  }, [userDetails])

  return (
    <ColorSchema>
      <Suspense
        fallback={
          <div
            style={{
              backgroundColor: 'rgba(var(--primary-background))',
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spinner id="tutorial-spinner" />
          </div>
        }
      >
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <TourProvider>
            <ToastContainer />
            <CssBaseline />
            {authContext.isAuthenticated &&
              ![
                '/termeni-si-conditii',
                '/politica-de-confidentialitate',
              ].includes(window.location.pathname) && <ParticlesDiv />}
            {layout === 'dashboard' &&
              authContext.isAuthenticated &&
              ![
                '/termeni-si-conditii',
                '/politica-de-confidentialitate',
              ].includes(window.location.pathname) && (
                <>
                  <Sidenav
                    color={sidenavColor}
                    routes={sidenavRoutes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                </>
              )}
            <AppRoutes />

            {notification !== undefined && (
              <Suspense fallback="">
                <Notification
                  updateUserDetails={authContext.updateUserDetails}
                  opened={authContext?.userDetails?.hasDetails}
                  notification={notification}
                  close={closeNotification}
                  userDetails={authContext.userDetails}
                />
              </Suspense>
            )}
          </TourProvider>
        </ThemeProvider>
      </Suspense>
    </ColorSchema>
  )
}
